import React, { useState } from "react";
import { motion } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";
import SEOHead from "../components/common/SEOHead";

const FAQ = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const seoData = {
    title: "FAQ - Livneu | Frequently Asked Questions",
    description:
      "Find answers to common questions about Livneu, our VTuber talents, auditions, merchandise, and services. Get quick answers to your queries about Sri Lanka's premier virtual entertainment agency.",
    image: "https://livneu.levelstair.com/ShareImage.png",
    url: "https://livneu.levelstair.com/faq",
    keywords:
      "FAQ, frequently asked questions, Livneu help, VTuber questions, support, audition FAQ, talent questions",
  };

  const faqs = [
    {
      category: "General Information",
      questions: [
        {
          question: "What is Livneu?",
          answer: "Livneu is Sri Lanka's premier VTuber (Virtual YouTuber) talent agency operated by LEVELSTAIR (Pvt) Ltd. We discover, train, and manage virtual entertainers who create content using digital avatars. Our mission is to establish a vibrant virtual entertainment ecosystem in South Asia while promoting local culture to global audiences."
        },
        {
          question: "Where is Livneu based?",
          answer: "Livneu is headquartered in Colombo, Sri Lanka, and operates across South Asia including Sri Lanka, India, Bangladesh, Pakistan, Nepal, Bhutan, and the Maldives. We provide services to talents and audiences throughout the region."
        },
        {
          question: "What languages do Livneu talents speak?",
          answer: "Our talents primarily create content in English to reach global audiences, with many also fluent in Sinhala, Tamil, Hindi, and other South Asian languages. This multilingual approach helps us connect with both local and international communities."
        }
      ]
    },
    {
      category: "Talent & Auditions",
      questions: [
        {
          question: "How can I become a Livneu talent?",
          answer: "We regularly hold open auditions for aspiring VTuber talents. Applicants must be 18 or older and demonstrate passion for virtual entertainment. Check our Auditions page for current opportunities and requirements. The process includes application review, interviews, and talent assessments."
        },
        {
          question: "What support do Livneu talents receive?",
          answer: "Our talents receive comprehensive support including: professional avatar design and rigging, technical setup assistance, content planning and strategy, marketing and promotion, merchandise development, event coordination, and ongoing training programs to help them grow their careers."
        },
        {
          question: "Can international applicants audition for Livneu?",
          answer: "Yes! While we're based in Sri Lanka, we welcome applicants from across South Asia and beyond. We're particularly interested in creators who can help bridge cultural gaps and bring diverse perspectives to virtual entertainment."
        },
        {
          question: "Do I need previous streaming experience to audition?",
          answer: "Previous streaming experience is helpful but not required. We value passion, creativity, and personality above all. Many successful VTubers started with no prior experience. We provide training and support to help new talents develop their skills."
        }
      ]
    },
    {
      category: "Services & Collaborations",
      questions: [
        {
          question: "Does Livneu offer business partnerships?",
          answer: "Yes, we welcome business partnerships, sponsorship opportunities, and brand collaborations. Our talents can participate in promotional content, product endorsements, and special events. Please contact our business development team for partnership inquiries."
        },
        {
          question: "Can Livneu talents appear at events or conventions?",
          answer: "Absolutely! Our talents regularly participate in gaming conventions, anime events, tech conferences, and cultural festivals across South Asia. We also organize virtual events and can arrange special appearances for corporate or community events."
        },
        {
          question: "Do you provide technical services for other agencies or independents?",
          answer: "Livneu offers consulting services including avatar design, Live2D rigging, 3D modeling, streaming setup, and technical support for independent creators and other agencies. Contact us to discuss your specific needs and pricing."
        }
      ]
    },
    {
      category: "Fan Support & Community",
      questions: [
        {
          question: "Can I send fan letters or gifts to Livneu talents?",
          answer: "We currently accept fan letters sent to our Colombo office. Please address letters to: Livneu, [Talent Name], Colombo, Sri Lanka. Physical gifts are not accepted at this time for security reasons. Digital fan art and messages through official social media channels are always welcome!"
        },
        {
          question: "How can I support Livneu talents?",
          answer: "You can support our talents by: watching and sharing their content, engaging positively in chat and comments, purchasing official merchandise, participating in community events, following their social media accounts, and respecting community guidelines to maintain a positive environment."
        },
        {
          question: "Are there official fan clubs or membership programs?",
          answer: "We're currently developing official fan club programs with exclusive benefits including early access to content, special merchandise, virtual meet-and-greets, and member-only events. Stay tuned to our announcements for launch details!"
        },
        {
          question: "Can fans create derivative works featuring Livneu talents?",
          answer: "Yes! We encourage fan creativity including fan art, music covers, clips, and other derivative works for non-commercial purposes. Please review our Derivative Works Guidelines for specific rules and ensure all fan content respects our community standards and applicable laws."
        }
      ]
    },
    {
      category: "Merchandise & Store",
      questions: [
        {
          question: "Where can I buy official Livneu merchandise?",
          answer: "Official merchandise is available through our online store and select retail partners in Sri Lanka. We offer shipping across South Asia and are expanding to international shipping. Products include apparel, accessories, collectibles, and digital goods."
        },
        {
          question: "Do you ship merchandise internationally?",
          answer: "We currently ship within South Asia with plans to expand international shipping to other regions. Shipping costs and delivery times vary by location. Check our store for current shipping options and restrictions to your area."
        },
        {
          question: "Can I request custom or personalized merchandise?",
          answer: "Limited custom merchandise options may be available for special orders or bulk purchases. Contact our merchandise team with your requirements. Please note that custom orders require minimum quantities and longer production times."
        }
      ]
    },
    {
      category: "Technical Support",
      questions: [
        {
          question: "I'm experiencing issues with the website or streams. How do I get help?",
          answer: "For technical issues, please contact our support team at support@livneu.levelstair.com with details about the problem, your device/browser information, and steps to reproduce the issue. We aim to respond within 24-48 hours."
        },
        {
          question: "Are there mobile apps for Livneu content?",
          answer: "We're currently developing mobile applications to enhance the viewing experience and provide exclusive content access. Beta testing will begin soon with registered community members. Follow our social media for app launch announcements."
        },
        {
          question: "What streaming platforms do Livneu talents use?",
          answer: "Our talents primarily stream on YouTube, with additional presence on Twitch, TikTok, and other platforms. We're also exploring regional platforms popular in South Asia to better reach local audiences. Check individual talent pages for their specific platform presence."
        }
      ]
    }
  ];

  const toggleFAQ = (categoryIndex, questionIndex) => {
    const faqId = `${categoryIndex}-${questionIndex}`;
    setOpenFAQ(openFAQ === faqId ? null : faqId);
  };

  return (
    <HelmetProvider>
      <div className="min-h-screen">
        <SEOHead seoData={seoData} />

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-6 overflow-hidden bg-gradient-faq">
          {/* Kawaii Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-pastel-purple/10 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-pastel-blue/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "2s" }}></div>
            <div className="absolute top-1/2 left-1/2 w-32 h-32 bg-kawaii-mint/10 rounded-full blur-2xl animate-pulse" style={{ animationDelay: "4s" }}></div>
          </div>

          <div className="container mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h1 className="text-5xl md:text-7xl font-title text-text-accent mb-6 leading-tight kawaii-glow">
                Frequently Asked Questions
              </h1>
              <motion.p
                className="text-xl md:text-2xl text-text-secondary mb-8 leading-relaxed font-body"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                Find quick answers to common questions about Livneu, our talents, services, and community
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* FAQ Content */}
        <section className="py-20 px-6">
          <div className="container mx-auto max-w-6xl">
          {faqs.map((category, categoryIndex) => (
            <motion.div
              key={categoryIndex}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
              className="mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-title text-center mb-8 text-text-accent kawaii-glow">
                {category.category}
              </h2>
              
              <div className="space-y-4">
                {category.questions.map((faq, questionIndex) => {
                  const faqId = `${categoryIndex}-${questionIndex}`;
                  const isOpen = openFAQ === faqId;
                  
                  return (
                    <motion.div
                      key={questionIndex}
                      initial={{ opacity: 0, x: -50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: questionIndex * 0.1 }}
                      className="glass-card rounded-xl border-pastel-purple/20 overflow-hidden hover:border-pastel-blue/30 transition-all duration-300"
                    >
                      <button
                        onClick={() => toggleFAQ(categoryIndex, questionIndex)}
                        className="w-full px-6 md:px-8 py-6 text-left hover:bg-surface-secondary/50 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-pastel-purple/50"
                      >
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg md:text-xl font-accent text-text-primary pr-4">
                            {faq.question}
                          </h3>
                          <motion.div
                            animate={{ rotate: isOpen ? 45 : 0 }}
                            transition={{ duration: 0.3 }}
                            className="flex-shrink-0"
                          >
                            <div className="w-6 h-6 flex items-center justify-center">
                              <div className="w-4 h-0.5 bg-pastel-purple absolute"></div>
                              <div className="w-0.5 h-4 bg-pastel-purple absolute"></div>
                            </div>
                          </motion.div>
                        </div>
                      </button>
                      
                      <motion.div
                        initial={false}
                        animate={{
                          height: isOpen ? "auto" : 0,
                          opacity: isOpen ? 1 : 0
                        }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 md:px-8 pb-6">
                          <p className="text-text-secondary leading-relaxed text-lg font-body">
                            {faq.answer}
                          </p>
                        </div>
                      </motion.div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Contact Section */}
        <motion.section 
          className="py-16 px-4"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <div className="container mx-auto max-w-4xl text-center">
            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-2xl p-8 md:p-12 border border-gray-700">
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
                Still Have Questions?
              </h2>
              <p className="text-xl text-gray-700 leading-relaxed mb-8">
                If you couldn't find the answer you're looking for, don't hesitate to reach out to our support team. 
                We're here to help and typically respond within 24-48 hours.
              </p>
              
              {/* <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                  <h3 className="text-xl font-bold text-purple-400 mb-3">General Inquiries</h3>
                  <p className="text-gray-700 mb-2">info@livneu.levelstair.com</p>
                  <p className="text-sm text-gray-400">Business partnerships, general questions</p>
                </div>
                <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                  <h3 className="text-xl font-bold text-purple-400 mb-3">Technical Support</h3>
                  <p className="text-gray-700 mb-2">support@livneu.levelstair.com</p>
                  <p className="text-sm text-gray-400">Website issues, streaming problems</p>
                </div>
              </div> */}
              
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <a
                  href="/contact"
                  className="inline-block bg-gradient-to-r from-purple-400 to-blue-500 text-black px-8 py-4 rounded-full text-xl font-bold shadow-2xl hover:shadow-cyan-500/25 transition-all duration-300"
                >
                  Contact Us
                </a>
              </motion.div>
            </div>
          </div>
        </motion.section>
        </section>
      </div>
    </HelmetProvider>
  );
};

export default FAQ;
